import React, { useState } from 'react';
import { v4 as uuid4 } from 'uuid';
import styled, { keyframes } from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import Gap from '@component/common/gap';
import Placeholder from '@component/common/placeholder';
import Canvas from '@design-system/canvas';
import Quote from '@design-system/quote';
import { IClozeExplanation } from '@datatype/iyagigo/api-type';
import { ETheme } from '@datatype/type';
import { normalizeExplanation, replaceDashWithTilde } from '@util/string';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  title: string;
  description: string;
  pattern: IClozeExplanation;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const AlternativeItemContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const pointerFloat = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
`;
const MeaningButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: ${size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT};
  cursor: pointer;
`;
const PointerContainer = styled.span`
  display: flex;
  animation: ${pointerFloat} 1.5s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PatternContent: React.FC<IProps> = ({ title, description, pattern }) => {
  const [meaningVisible, setMeaningVisible] = useState(false);
  const example = pattern.example?.[0];
  const alternatives = pattern.alternative ?? [];
  const AlternativeItems = alternatives.map((item) => {
    const alternativeExample = item.example?.[0];
    return (
      <AlternativeItemContainer key={uuid4()}>
        <Gap height={size.MARGIN_1} />
        <Quote width='100%'>
          <TextBlock
            text={replaceDashWithTilde(item.keyword)}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <TextBlock
            text={replaceDashWithTilde(item.meaning?.join(', ') ?? '')}
            design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
          />
          {item.usage?.trim() && (
            <>
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text={normalizeExplanation(replaceDashWithTilde(item.usage), color.BLACK_2)}
                design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
              />
            </>
          )}
          {alternativeExample?.sentence && alternativeExample.translation && (
            <>
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text={alternativeExample.sentence}
                design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
              />
              <TextBlock
                text={alternativeExample.translation}
                design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
              />
            </>
          )}
        </Quote>
      </AlternativeItemContainer>
    );
  });

  return (
    <Wrapper>
      <TextBlock
        text={title}
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      {/*<Gap height={size.MARGIN_1} />*/}
      <TextBlock
        text={description}
        design={{ text: { size: 'medium', color: color.BLACK_4 }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_2} />
      <Canvas theme={ETheme.PAPER} shadow width='100%' padding={size.CONTENT_PADDING} alignX='center'>
        <TextBlock
          text={replaceDashWithTilde(pattern.keyword)}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'center' } }}
        />
        <Gap height={size.MARGIN_1} />
        <Placeholder
          visible={meaningVisible}
          placeholder={
            <MeaningButtonContainer onClick={() => setMeaningVisible(true)}>
              <PointerContainer>
                <IconBlock icon='touch-app' design={{ icon: { size: 'small', color: color.GRAY_1 } }} />
              </PointerContainer>
              <TextBlock text='뜻 보기' design={{ text: { size: 'medium', color: color.GRAY_1 } }} />
            </MeaningButtonContainer>
          }>
          <TextBlock
            text={replaceDashWithTilde(pattern.meaning?.join(', ') ?? '')}
            design={{ text: { size: 'medium', color: color.BROWN_1 }, box: { alignX: 'center' } }}
          />
        </Placeholder>
      </Canvas>
      {meaningVisible && pattern.usage?.trim() && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock
            text='쓰임'
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <Gap height={'5px'} />
          <Quote width='100%'>
            <TextBlock
              text={normalizeExplanation(replaceDashWithTilde(pattern.usage), color.BLACK_2)}
              design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
            />
          </Quote>
        </>
      )}
      {meaningVisible && example?.sentence && example.translation && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock
            text='예문'
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <Gap height={'5px'} />
          <Quote width='100%'>
            <TextBlock
              text={example.sentence}
              design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
            />
            <TextBlock
              text={example.translation}
              design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
            />
          </Quote>
        </>
      )}
      {meaningVisible && alternatives.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock
            text='함께 알아둘 문형'
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          {AlternativeItems}
        </>
      )}
    </Wrapper>
  );
};

export default PatternContent;
